// Shared hook — see backlog DOCS-33. PhotoAttachments and DocumentViewPage's
// PhotoSheetSection each used to resolve DocPhoto[] paths to signed URLs
// inline, with no .catch() on either — a resolve failure left a photo tile
// silently blank forever, the exact anti-pattern useAsyncData.ts (backlog
// UX-1) exists to prevent, just not applied here. This is the shared fix for
// both, not two separate ones.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { resolvePhotoUrl } from "@/lib/photoUpload";
import type { DocPhoto } from "@/lib/types";

/**
 * Resolves each photo's stored bucket path to a fresh signed URL (the
 * account-files bucket is private — see photoUpload.ts's resolvePhotoUrl,
 * never persisted so a document viewed weeks later still displays).
 * Incremental: only resolves ids not already in the returned map, so
 * reordering photos or editing a caption elsewhere doesn't re-resolve
 * already-loaded ones.
 *
 * `preResolved` (backlog PRINT-26): when passed — even as `{}` — this
 * short-circuits network resolution entirely and returns that map as-is.
 * The print-only route (PrintDocumentPage) has no Supabase session to
 * resolve these itself; its Edge Function (print-doc-data) resolves every
 * photo's signed URL server-side and hands the whole map to this hook
 * instead. Every other caller omits the argument (stays `undefined`) and
 * gets today's unchanged authenticated-resolution behavior.
 */
export function useResolvedPhotoUrls(photos: DocPhoto[], preResolved?: Record<string, string>): Record<string, string> {
  const t = useT();
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (preResolved !== undefined) return;
    let cancelled = false;
    const missing = photos.filter((p) => !(p.id in resolvedUrls));
    if (missing.length === 0) return;
    Promise.all(missing.map((p) => resolvePhotoUrl(p.url).then((url) => [p.id, url] as const)))
      .then((pairs) => {
        if (cancelled) return;
        setResolvedUrls((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("useResolvedPhotoUrls: resolvePhotoUrl failed", error);
        toast.error(t("photos.resolveError"));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, preResolved]);

  return preResolved !== undefined ? preResolved : resolvedUrls;
}
